import { CheckCircleIcon } from "@phosphor-icons/react";
import type { FocusEventHandler, MouseEventHandler, ReactNode } from "react";
import { Suspense, lazy } from "react";

import { AnimatedLoader } from "@/components/Loaders/AnimatedLoader";
import { PageLoader } from "@/components/Loaders/PageLoader";
import { TextField } from "@/components/TextField/TextField";

import { DialCodeButton } from "./DialCodeButton";
import type { PhoneCountry } from "./types";
import { usePhoneInput } from "./usePhoneInput";

const CountryPickerDrawer = lazy(() => import("./CountryPickerDrawer"));

export type TelInputProps = {
  value: string;
  onChange: (value: string) => void;
  onBlur?: FocusEventHandler<HTMLInputElement>;
  onClick?: MouseEventHandler<HTMLDivElement>;
  onDialCodeClick?: () => void;
  label: ReactNode;
  error?: boolean;
  helperText?: ReactNode;
  fullWidth?: boolean;
  autoComplete?: string;
  defaultCountry?: PhoneCountry;
};

export function TelInput({
  value,
  onChange,
  onBlur,
  onClick,
  onDialCodeClick,
  label,
  error,
  helperText,
  fullWidth,
  autoComplete = "tel",
  defaultCountry = "IQ",
}: TelInputProps) {
  const {
    inputRef,
    country,
    nationalDigits,
    isValid,
    drawerOpen,
    setDrawerOpen,
    handleChange,
    handleSelectCountry,
  } = usePhoneInput({ value, onChange, defaultCountry });

  return (
    <>
      <div className="flex items-start gap-2 text-base">
        <DialCodeButton
          country={country}
          onOpen={() => {
            onDialCodeClick?.();
            setDrawerOpen(true);
          }}
        />
        <TextField
          inputRef={inputRef}
          value={nationalDigits}
          onChange={(e) => handleChange(e.target.value)}
          onBlur={onBlur}
          onClick={onClick}
          label={label}
          error={error}
          helperText={helperText}
          fullWidth={fullWidth}
          autoComplete={autoComplete}
          inputProps={{ inputMode: "numeric" }}
          endAdornment={
            isValid ? (
              <CheckCircleIcon
                size={16}
                data-testid="tel-input-valid-icon"
                className="fill-core-accent-500"
                aria-hidden="true"
              />
            ) : undefined
          }
        />
      </div>
      <Suspense
        fallback={
          <PageLoader>
            <AnimatedLoader icon="buildings" />
          </PageLoader>
        }
      >
        <CountryPickerDrawer
          open={drawerOpen}
          selectedCountry={country}
          onSelect={handleSelectCountry}
          onClose={() => setDrawerOpen(false)}
        />
      </Suspense>
    </>
  );
}
